const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

const SCRAPER_FALLBACK_URL = 'https://scraper.auction.ai/';
const SCRAPER_FALLBACK_ORIGIN = 'https://scraper.auction.ai';

const isLocalHostname = (hostname) => {
  const normalized = String(hostname || '').trim().toLowerCase();
  if (!normalized) return false;
  if (LOCAL_HOSTNAMES.has(normalized)) return true;
  return normalized.endsWith('.localhost');
};

const isProbablyPublicDomain = (hostname) => {
  const normalized = String(hostname || '').trim().toLowerCase();
  if (!normalized) return false;
  // If it has at least one dot and doesn't end with .localhost, 
  // and is not in LOCAL_HOSTNAMES, we'll treat it as potentially public.
  return normalized.includes('.') && !isLocalHostname(normalized);
};

const hasWindowLocation = () => (
  typeof window !== 'undefined' &&
  window &&
  window.location &&
  typeof window.location.origin === 'string'
);

const getRuntimeOrigin = () => (
  hasWindowLocation()
    ? window.location.origin
    : SCRAPER_FALLBACK_ORIGIN
);

const getRuntimeHostname = () => (
  hasWindowLocation()
    ? window.location.hostname
    : ''
);

const isDevelopmentRuntime = () => (
  typeof process !== 'undefined' &&
  process &&
  process.env &&
  process.env.NODE_ENV === 'development'
);

const toHttpUrl = (rawValue, baseOrigin, fallbackUrl) => {
  try {
    const parsed = new URL(String(rawValue || '').trim(), baseOrigin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return new URL(fallbackUrl);
    }
    return parsed;
  } catch (_err) {
    return new URL(fallbackUrl);
  }
};

const shouldForceFallback = (targetHostname, currentHostname) => {
  if (!isLocalHostname(targetHostname)) {
    return false;
  }
  // In production, a localhost scraper URL is almost always a misconfig
  // (e.g. VM or deployed app opening the wrong host), so force fallback.
  if (!isDevelopmentRuntime()) {
    return true;
  }
  
  // If we are on a VM or remote environment (accessed via port forward or IP),
  // currentHostname might be 'localhost' but we should still force fallback
  // if we're not REALLY in a local dev environment where everything is local.
  // We'll use a heuristic: if we are on localhost but the app was built to 
  // expect a public scraper, we should probably use the public scraper.
  
  // If we're on localhost but current browser doesn't seem to be a local dev
  // environment (this is hard to detect, but we can try to see if the
  // fallback itself is a public domain).
  let fallbackHostname = '';
  try {
    fallbackHostname = new URL(SCRAPER_FALLBACK_URL).hostname;
  } catch (_e) {
    // ignore
  }

  if (isLocalHostname(currentHostname) && isProbablyPublicDomain(fallbackHostname)) {
    // If we're on localhost and the fallback is a public domain, 
    // it's likely we want the public domain unless specifically overridden
    // to another local service.
    return true;
  }

  // Fallback if we are on an IP (remote access) and target is localhost
  return !isLocalHostname(currentHostname);
};

export const resolveScraperUrl = (configuredValue) => {
  const parsed = toHttpUrl(
    configuredValue || SCRAPER_FALLBACK_URL,
    getRuntimeOrigin(),
    SCRAPER_FALLBACK_URL,
  );
  if (shouldForceFallback(parsed.hostname, getRuntimeHostname())) {
    return new URL(SCRAPER_FALLBACK_URL);
  }
  return parsed;
};

export const resolveScraperOrigin = (configuredOrigin) => {
  const parsed = toHttpUrl(
    configuredOrigin || SCRAPER_FALLBACK_ORIGIN,
    getRuntimeOrigin(),
    SCRAPER_FALLBACK_ORIGIN,
  );
  if (shouldForceFallback(parsed.hostname, getRuntimeHostname())) {
    return SCRAPER_FALLBACK_ORIGIN;
  }
  return parsed.origin;
};
